import { useState, useEffect, useRef, useCallback } from "react";
import useThrottledOnScroll from "./useThrottledOnScroll";
import {
  isEmbedded,
  getParentScrollTop,
  subscribeParentScroll,
} from "../utils/embeddedScroll.js";

// When embedded we can't read the parent's viewport height (the bridge only sends the
// scroll offset), so use a fixed activation line roughly under the sticky tab bar.
const EMBEDDED_ACTIVE_OFFSET = 120;

const useScrollSpy = ({ items = [], target = window } = {}) => {
  const itemsWithNodeRef = useRef([]);
  useEffect(() => {
    itemsWithNodeRef.current = getItemsClient(items);
  }, [items]);

  const [activeState, setActiveState] = useState(null);

  const findActiveIndex = useCallback(() => {
    const embedded = isEmbedded();
    // In an embedded pym iframe the local window never scrolls; the parent does, so
    // use the parent's scroll offset (in child content coordinates) instead.
    const scrollTop = embedded
      ? getParentScrollTop()
      : document.documentElement.scrollTop;
    const activationOffset = embedded
      ? EMBEDDED_ACTIVE_OFFSET
      : document.documentElement.clientHeight / 8;

    let active;
    for (let i = itemsWithNodeRef.current.length - 1; i >= 0; i -= 1) {
      // No hash if we're near the top of the page
      if (scrollTop < 200) {
        active = { hash: null };
        break;
      }

      const item = itemsWithNodeRef.current[i];

      if (item.node && item.node.offsetTop < scrollTop + activationOffset) {
        active = item;
        break;
      }
    }

    if (active && activeState !== active.hash) {
      setActiveState(active.hash);
    }
  }, [activeState]);

  useThrottledOnScroll(items.length > 0 ? findActiveIndex : null, 166);

  // The local scroll event never fires inside a full-height pym iframe, so recompute
  // the active section whenever the parent reports a new scroll offset.
  useEffect(() => {
    if (!isEmbedded() || items.length === 0) return undefined;
    findActiveIndex();
    return subscribeParentScroll(findActiveIndex);
  }, [findActiveIndex, items.length]);

  return activeState;
};

const getItemsClient = (items) =>
  items.map(({ hash }) => ({ hash, node: document.getElementById(hash) }));

export default useScrollSpy;
